/**
 * Following a close of a ticket (ADR 0182; v0.9.0's P3 UI), or a reopen
 * (P9; ADR 0197): `GET /api/v1/ticket-actions/{id}`, every 2 seconds until
 * it's finished. The popup's Close ticket, a dashed card's Close, the Log
 * form's Reopen and log, and the day view (which refreshes when a close under
 * way ends) share each action's one query.
 */
import { useQuery } from "@tanstack/react-query";

import { retryUnlessRefused } from "../../api/client";
import type { TicketAction } from "../../api/types";
import { useSession } from "../../app/session-context";

/** How often a close under way is asked about. */
export const ACTION_POLL_MS = 2_000;

/** Still to run, or running. */
export function isUnfinished(action: TicketAction | undefined): boolean {
  return action !== undefined && (action.state === "pending" || action.state === "running");
}

export function ticketActionQuery(actionId: string) {
  return ["ticket-action", actionId] as const;
}

/** A close or a reopen, followed until it's finished; nothing while there's none. */
export function useTicketAction(actionId: string | null) {
  const { api } = useSession();
  return useQuery({
    queryKey: ["ticket-action", actionId],
    queryFn: () => api.getTicketAction(actionId ?? ""),
    enabled: actionId !== null,
    refetchInterval: (query) => (isUnfinished(query.state.data) ? ACTION_POLL_MS : false),
    retry: retryUnlessRefused,
  });
}
